import {Helmet} from '@dr.pogodin/react-helmet';
import {openDeleteDialog, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {isArray} from 'ramda-adjunct';

import {useDeleteDueDateSettingMutation} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {catchUnhandledDataGridActions, DmsDataGrid, handleApiError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

const PREDEFINED_COLUMNS = ['branch'] as const;

export function BusinessCaseDueDatesOverview() {
  const navigate = useNavigate();

  const [deleteBranchDueDateSettings] = useDeleteDueDateSettingMutation();

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    getColumnData,
    refreshData,
  }) => {
    const id = isArray(rowId) ? rowId[0] : rowId;
    const name = getColumnData<{label: string}>('branch')?.value?.label;

    match(actionKey)
      .with('EDIT', 'DETAIL', () => {
        navigate(composePath(settingsRoutes.businessCaseDueDatesDetail, {params: {id}}));
      })
      .with('DELETE', () => {
        openDeleteDialog({
          onConfirm: () =>
            deleteBranchDueDateSettings({id})
              .unwrap()
              .then(() => refreshData())
              .then(() =>
                showNotification.success(i18n.t('general.notifications.successfullyDeleted'))
              )
              .catch(handleApiError),
          text: i18n.t('entity.accounting.labels.deleteDefinitionDeleteText', {name}),
        });
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const title = i18n.t('page.settings.labels.dueDates');

  return (
    <>
      <Helmet title={title} />
      <SettingsTemplate
        header={{
          title,
          breadcrumbs: [
            {
              label: i18n.t('page.businessCases.title'),
              href: settingsRoutes.businessCase,
            },
          ],
          actions: [
            {
              type: 'button',
              onClick: () => navigate(settingsRoutes.businessCaseDueDatesNew),
              title: i18n.t('page.accounting.labels.newDefinition'),
            },
          ],
        }}
        data-testid={testIds.settings.businessCaseDueDates('settingsTemplate')}
      >
        <DmsDataGrid
          gridCode="checkout-due-date-settings"
          actionCallback={actionCallback}
          predefinedColumns={PREDEFINED_COLUMNS}
          data-testid={testIds.settings.businessCaseDueDates('employee-profession')}
        />
      </SettingsTemplate>
    </>
  );
}
